import { template } from "@ember/template-compiler";
import { hash } from '@ember/helper';
import { arrow } from '@floating-ui/dom';
import { element } from 'ember-element-helper';
import { modifier } from 'ember-modifier';
import { cell } from 'ember-resources';
import { Velcro } from 'ember-velcro';
import { Portal } from './portal.gts';
import { TARGETS } from './portal-targets.gts';
import type { TOC } from '@ember/component/template-only';
import type { Middleware, MiddlewareData } from '@floating-ui/dom';
import type { ModifierLike, WithBoundArgs } from '@glint/template';
import type { Signature as HookSignature } from 'ember-velcro/modifiers/velcro';
export interface Signature {
    Args: {
        /**
     * See the Floating UI's [flip docs](https://floating-ui.com/docs/flip) for possible values.
     *
     * This argument is forwarded to `ember-velcro`'s `<Velcro>` component.
     */ flipOptions?: HookSignature['Args']['Named']['flipOptions'];
        /**
     * Array of one or more objects to add to Floating UI's list of [middleware](https://floating-ui.com/docs/middleware)
     *
     * This argument is forwarded to `ember-velcro`'s `<Velcro>` component.
     */ middleware?: HookSignature['Args']['Named']['middleware'];
        /**
     * See the Floating UI's [offset docs](https://floating-ui.com/docs/offset) for possible values.
     *
     * This argument is forwarded to `ember-velcro`'s `<Velcro>` component.
     */ offsetOptions?: HookSignature['Args']['Named']['offsetOptions'];
        /**
     * One of the possible [`placements`](https://floating-ui.com/docs/computeposition#placement). The default is 'bottom'.
     *
     * Possible values are
     * - top
     * - bottom
     * - right
     * - left
     *
     * And may optionally have `-start` or `-end` added to adjust position along the side.
     *
     * This argument is forwarded to `ember-velcro`'s `<Velcro>` component.
     */ placement?: `${'top' | 'bottom' | 'left' | 'right'}${'' | '-start' | '-end'}`;
        /**
     * See the Floating UI's [shift docs](https://floating-ui.com/docs/shift) for possible values.
     *
     * This argument is forwarded to `ember-velcro`'s `<Velcro>` component.
     */ shiftOptions?: HookSignature['Args']['Named']['shiftOptions'];
        /**
     * CSS position property, either `fixed` or `absolute`.
     *
     * Pros and cons of each strategy are explained on [Floating UI's Docs](https://floating-ui.com/docs/computePosition#strategy)
     *
     * This argument is forwarded to `ember-velcro`'s `<Velcro>` component.
     */ strategy?: HookSignature['Args']['Named']['strategy'];
        /**
     * By default, the popover is portaled.
     * If you don't control your CSS, and the positioning of the popover content
     * is misbehaving, you may pass "@inline={{true}}" to opt out of portalling.
     *
     * Inline may also be useful in nested menus, where you know exactly how the nesting occurs
     */ inline?: boolean;
    };
    Blocks: {
        default: [{
                hook: ModifierLike<HookSignature>;
                Content: WithBoundArgs<typeof Content, 'loop'>;
                data: MiddlewareData;
                arrow: WithBoundArgs<ModifierLike<AttachArrowSignature>, 'arrowElement' | 'data'>;
            }];
    };
}
function getElementTag(tagName1: undefined | string) {
    return tagName1 || 'div';
}
/**
 * Allows lazy evaluation of the portal target (do nothing until rendered)
 * This is useful because the algorithm for finding the portal target isn't cheap.
 */ const Content: TOC<{
    Element: HTMLDivElement;
    Args: {
        loop: ModifierLike<{
            Element: HTMLElement;
        }>;
        inline?: boolean;
        /**
     * By default the popover content is wrapped in a div.
     * You may change this by supplying the name of an element here.
     *
     * For example:
     * ```gjs
     * <Popover as |p|>
     *  <p.Content @as="dialog">
     *    this is now focus trapped
     *  </p.Content>
     * </Popover>
     * ```
     */ as?: string;
    };
    Blocks: {
        default: [];
    };
}> = template(`
  {{#let (element (getElementTag @as)) as |El|}}
    {{#if @inline}}
      {{! @glint-ignore
            https://github.com/tildeio/ember-element-helper/issues/91
            https://github.com/typed-ember/glint/issues/610
      }}
      <El {{@loop}} ...attributes>
        {{yield}}
      </El>
    {{else}}
      <Portal @to={{TARGETS.popover}}>
        {{! @glint-ignore
              https://github.com/tildeio/ember-element-helper/issues/91
              https://github.com/typed-ember/glint/issues/610
        }}
        <El {{@loop}} ...attributes>
          {{yield}}
        </El>
      </Portal>
    {{/if}}
  {{/let}}
`, {
    eval () {
        return eval(arguments[0]);
    }
});
interface AttachArrowSignature {
    Element: HTMLElement;
    Args: {
        Named: {
            arrowElement: ReturnType<typeof ArrowElement>;
            data?: {
                middlewareData?: MiddlewareData;
                placement?: Placement;
            };
        };
    };
}
const arrowSides = {
    top: 'bottom',
    right: 'left',
    bottom: 'top',
    left: 'right'
};
type Direction = 'top' | 'bottom' | 'left' | 'right';
type Placement = `${Direction}${'' | '-start' | '-end'}`;
const attachArrow = modifier<AttachArrowSignature>((element1, _1: [], named1)=>{
    if (element1 === named1.arrowElement.current) {
        if (!named1.data) return;
        if (!named1.data.middlewareData) return;
        let { arrow: arrow1 } = named1.data.middlewareData;
        let { placement: placement1 } = named1.data;
        if (!arrow1) return;
        if (!placement1) return;
        let { x: arrowX1, y: arrowY1 } = arrow1;
        let otherSide1 = (placement1 as Placement).split('-')[0] as Direction;
        let staticSide1 = arrowSides[otherSide1];
        Object.assign(named1.arrowElement.current.style, {
            left: arrowX1 != null ? `${arrowX1}px` : '',
            top: arrowY1 != null ? `${arrowY1}px` : '',
            right: '',
            bottom: '',
            [staticSide1]: '-4px'
        });
        return;
    }
    (async ()=>{
        await Promise.resolve();
        named1.arrowElement.set(element1);
    })();
});
const ArrowElement: () => ReturnType<typeof cell> = ()=>cell<HTMLElement>();
function maybeAddArrow(middleware1: Middleware[] | undefined, element1: Element | undefined) {
    let result1 = [
        ...(middleware1 || [])
    ];
    if (element1) {
        result1.push(arrow({
            element: element1
        }));
    }
    return result1;
}
function flipOptions(options1: HookSignature['Args']['Named']['flipOptions']) {
    return {
        elementContext: 'reference',
        ...options1
    };
}
export const Popover: TOC<Signature> = template(`
  {{#let (ArrowElement) as |arrowElement|}}
    <Velcro
      @placement={{@placement}}
      @strategy={{@strategy}}
      @middleware={{maybeAddArrow @middleware arrowElement.current}}
      @flipOptions={{flipOptions @flipOptions}}
      @shiftOptions={{@shiftOptions}}
      @offsetOptions={{@offsetOptions}}
      as |velcro|
    >
      {{yield
        (hash
          hook=velcro.hook
          Content=(component Content loop=velcro.loop inline=@inline)
          data=velcro.data
          arrow=(modifier attachArrow arrowElement=arrowElement data=velcro.data)
        )
      }}
    </Velcro>
  {{/let}}
`, {
    eval () {
        return eval(arguments[0]);
    }
});
export default Popover;
